import type ArrayProxy from '@ember/array/proxy';
import { action } from '@ember/object';
import { service } from '@ember/service';
import type Store from '@ember-data/store';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { addDays, subMinutes, fromUnixTime } from 'date-fns';
import { format, formatInTimeZone } from 'date-fns-tz';
import type AbilitiesService from 'ember-can/services/abilities';
import { dropTask, task } from 'ember-concurrency';
import type DeskModel from 'garaje/models/desk';
import type EmployeeModel from 'garaje/models/employee';
import type FloorModel from 'garaje/models/floor';
import { getIdFromURN } from 'garaje/models/generic-resource';
import type MapFeatureModel from 'garaje/models/map-feature';
import type NeighborhoodModel from 'garaje/models/neighborhood';
import type ReservationModel from 'garaje/models/reservation';
import type AjaxService from 'garaje/services/ajax';
import { ConflictError } from 'garaje/services/ajax/errors';
import type CurrentAdminService from 'garaje/services/current-admin';
import type DeskLendingService from 'garaje/services/desk-lending';
import type FeatureFlagsService from 'garaje/services/feature-flags';
import type FlashMessagesService from 'garaje/services/flash-messages';
import { ResourceType, AvailabilityStatus } from 'garaje/services/live-map';
import type LiveMapService from 'garaje/services/live-map';
import type StateService from 'garaje/services/state';
import { LENDING_STATUS } from 'garaje/utils/enums';
import urlBuilder from 'garaje/utils/url-builder';
import { cached } from 'tracked-toolbox';

/**
 * Next desk assignment data from GraphQL search results
 */
interface NextAssignment {
  deskId: string;
  deskName: string;
  startTime: string;
}

// Coerces a value to a positive integer, returning null otherwise. Tightens
// the historical `Number(x); !Number.isNaN(...)` pattern: rejects "" (which
// Number coerces to 0), "0", non-integers, and negatives so we never send
// nonsensical ids to the backend.
function toPositiveIntOrNull(value: unknown): number | null {
  if (value == null) return null;
  const n = Number(value);
  return Number.isInteger(n) && n > 0 ? n : null;
}

// Pulls the `detail` field off a 409 payload (e.g. "BOOKER_EXISTS",
// "DESK_HAS_PLANNING_ASSIGNMENTS") so callers can route to a specific
// user-facing message.
function conflictDetail(error: unknown): string | null {
  if (!(error instanceof ConflictError)) return null;
  const payload = error.payload as { detail?: string } | null | undefined;
  return payload?.detail ?? null;
}

interface DeskPopupComponentArgs {
  feature: MapFeatureModel;
  desks: ArrayProxy<DeskModel>;
  reservations: ArrayProxy<ReservationModel>;
  employees: ArrayProxy<EmployeeModel>;
  selectedDate: Date;
  selectedTime?: number;
  selectedEndTime?: number;
  setNeighborhoodToView: (neighborhoodId: string) => void;
  currentUserReservation?: ReservationModel;
  loadReservations?: () => void;
  loadAvailableDesks?: () => void;
  onCancel?: () => void;
  selectedNextAssignment?: NextAssignment;
  availableDesks?: DeskModel[];
  isAtCapacity?: boolean;
}

export default class DeskPopup extends Component<DeskPopupComponentArgs> {
  @service declare state: StateService;
  @service declare store: Store;
  @service declare liveMap: LiveMapService;
  @service declare ajax: AjaxService;
  @service declare flashMessages: FlashMessagesService;
  @service declare currentAdmin: CurrentAdminService;
  @service declare featureFlags: FeatureFlagsService;
  @service declare deskLending: DeskLendingService;
  @service declare abilities: AbilitiesService;

  static readonly CANCEL_LENDING_LOOKUP_DAYS = 90;

  @tracked showNeighborhoodPopup = false;
  @tracked adminLendModalOpen = false;
  @tracked adminCancelLendingModalOpen = false;
  @tracked adminCancelLendingAllowedDates: string[] = [];

  get selectedDateFormatted(): string {
    const { selectedDate } = this.args;
    return format(subMinutes(selectedDate, this.state.minutesBetweenTimezones(selectedDate)), 'MMMM dd, yyyy');
  }

  get floor(): FloorModel | null {
    const desk = this.getDeskById(this.args.feature.externalId);
    if (!desk) return null;

    return this.store.peekRecord('floor', desk.belongsTo('floor').id());
  }

  get neighborhood(): NeighborhoodModel | null {
    const { feature } = this.args;
    const desk = this.getDeskById(feature.externalId);
    if (!desk) return null;

    const neighborhoodId = desk.belongsTo('neighborhood').id();
    if (neighborhoodId) {
      return this.store.peekRecord('neighborhood', neighborhoodId);
    }
    return null;
  }

  @action
  getLenderName(deskId: string): string | null {
    if (!this.featureFlags.isEnabled('reverse-hoteling')) return null;
    const deskState = this.liveMap.getResourceStateByIdAndType(deskId, ResourceType.DESK);
    if (!deskState?.lender) return null;
    return deskState.lender.name || 'An employee';
  }

  @action
  getReservationByDeskId(deskId: string): ReservationModel | undefined {
    return this.args.reservations.find((res) => res.belongsTo('desk').id() == deskId);
  }

  @action
  getDeskById(deskId: string): DeskModel | null {
    return this.store.peekRecord('desk', deskId);
  }

  @action
  getEmployeeByEmail(email: string): EmployeeModel | undefined {
    const employees = this.store.peekAll('employee');
    return employees.find((employee) => employee.email === email);
  }

  /**
   * Gets the correct employee for a desk using the liveMap service as the primary source.
   * Falls back to reservation/desk email lookup ONLY when liveMap has no occupant data.
   * When liveMap identifies a specific employee but that employee isn't in the store yet,
   * returns undefined so the template can use getLiveMapProfileData as a fallback.
   */
  @action
  getEmployeeForDesk(deskId: string, reservationEmail?: string, assignedToEmail?: string): EmployeeModel | undefined {
    const employees = this.store.peekAll('employee');

    const deskState = this.liveMap.getResourceStateByIdAndType(deskId, ResourceType.DESK);
    if (deskState) {
      const employeeId = getIdFromURN(deskState.status?.reservedFor) || getIdFromURN(deskState.assignedTo);
      if (employeeId) {
        const employeeById = employees.find((e) => e.id === employeeId);
        if (employeeById) return employeeById;
      }
    }

    const email = reservationEmail || assignedToEmail;
    if (email) {
      return employees.find((e) => e.email === email);
    }

    return undefined;
  }

  /**
   * Gets profile display data directly from the liveMap service.
   * Used as a fallback when the employee record hasn't been loaded into the store yet.
   */
  @action
  getLiveMapProfileData(deskId: string): { id: string | null; name: string; avatarUrl: string | null } | null {
    const deskState = this.liveMap.getResourceStateByIdAndType(deskId, ResourceType.DESK);
    if (!deskState) return null;

    const profileData = deskState.status?.reservedForProfileData || deskState.assignedToProfileData;
    if (!profileData?.name) return null;

    return {
      id: profileData.id,
      name: profileData.name,
      avatarUrl: profileData.avatarUrl,
    };
  }

  @action
  getActiveNeighborhood(deskId: string): NeighborhoodModel | null {
    const deskState = this.liveMap.getResourceStateByIdAndType(deskId, ResourceType.DESK);
    if (deskState && 'activeNeighborhoodId' in deskState) {
      // activeNeighborhoodId is present, null means no active neighbourhood today
      if (!deskState.activeNeighborhoodId) return null;
      return this.store.peekRecord('neighborhood', String(deskState.activeNeighborhoodId));
    }
    // activeNeighborhoodId is absent, fall back to static relationship
    const staticNeighborhoodId = this.getDeskById(deskId)?.belongsTo('neighborhood').id();
    if (!staticNeighborhoodId) return null;
    return this.store.peekRecord('neighborhood', staticNeighborhoodId);
  }

  @action
  partialDayFormat(reservation: ReservationModel): string {
    const startTime = fromUnixTime(reservation.startTime);
    const formattedStartTime = format(subMinutes(startTime, this.state.minutesBetweenTimezones(startTime)), 'h:mma');
    const endTime = fromUnixTime(reservation.endTime);
    const formattedEndTime = format(subMinutes(endTime, this.state.minutesBetweenTimezones(endTime)), 'h:mma');
    return `${formattedStartTime} - ${formattedEndTime}`;
  }

  /**
   * Formats the startTime from nextAssignment (GraphQL).
   * Only shows startTime since endTime is not available from GraphQL yet.
   */
  get nextAssignmentTimeFormat(): string | null {
    const { selectedNextAssignment } = this.args;
    if (!selectedNextAssignment?.startTime) {
      return null;
    }

    const startTime = new Date(selectedNextAssignment.startTime);
    return format(subMinutes(startTime, this.state.minutesBetweenTimezones(startTime)), 'h:mma');
  }

  /**
   * Checks if the popup should use nextAssignment time instead of reservation time.
   * This happens when an employee was selected from search results with nextAssignment data.
   */
  get shouldUseNextAssignmentTime(): boolean {
    return !!this.args.selectedNextAssignment?.startTime;
  }

  @action
  showScheduleSharingDisabled(employee: EmployeeModel, desk: DeskModel): boolean {
    return (
      !(desk.assignedTo === employee.email && employee.belongsTo('user').id() === this.state.currentUser.id) &&
      !(this.args.currentUserReservation?.belongsTo('user').id() === employee.belongsTo('user').id()) &&
      (employee.hideFromEmployeeSchedule || !this.state.currentLocation.employeeScheduleEnabled)
    );
  }

  @action
  onNeighborhoodClick(neighborhoodId: string, e: Event): void {
    e.stopPropagation();
    this.args.setNeighborhoodToView(neighborhoodId);
    this.showNeighborhoodPopup = true;
  }

  @action
  getDeskAvailabilityStatus(deskId: string): AvailabilityStatus | null {
    const desk = this.getDeskById(deskId);
    if (desk && desk.enabled === false) {
      return AvailabilityStatus.UNAVAILABLE;
    }

    const liveMapState = this.liveMap.getResourceStateByIdAndType(deskId, ResourceType.DESK);
    if (liveMapState) {
      return liveMapState.status.availabilityStatus;
    }

    // No state for this desk: if the live array has other entries, the BE intentionally
    // omitted this desk (it is available). If the live array is entirely empty, the BE
    // returned no data (e.g. API error for certain date ranges) — treat as unavailable.
    return this.liveMap.resourceStates.length > 0 ? AvailabilityStatus.AVAILABLE : AvailabilityStatus.UNAVAILABLE;
  }

  get isAccessible(): boolean {
    const desk = this.getDeskById(this.args.feature.externalId);

    if (!desk) {
      return false;
    }

    const { availableDesks } = this.args;

    // undefined means the task hasn't run yet or isn't used in this context — default to accessible
    if (availableDesks === undefined) {
      return true;
    }

    // empty array means the task ran but returned nothing — desk is not accessible
    // populated array — desk is accessible only if it appears in the list
    return availableDesks.includes(desk);
  }

  /**
   * Returns true when the live map marks a desk as UNAVAILABLE due to neighbourhood
   * restriction (activeNeighborhoodId is set, no occupant)
   */
  @action
  isNeighborhoodRestricted(deskId: string): boolean {
    const deskState = this.liveMap.getResourceStateByIdAndType(deskId, ResourceType.DESK);
    if (!deskState) return false;
    return (
      deskState.status?.availabilityStatus === AvailabilityStatus.UNAVAILABLE &&
      !!deskState.activeNeighborhoodId &&
      !deskState.assignedTo &&
      !deskState.status?.reservedFor
    );
  }

  /**
   * Checks if the desk has an occupant (assigned or reserved) from liveMap data,
   * regardless of availabilityStatus. A desk can be "AVAILABLE" in status but still
   * have a schedule-based assignment that should be displayed.
   */
  @action
  hasLiveMapOccupant(deskId: string): boolean {
    const deskState = this.liveMap.getResourceStateByIdAndType(deskId, ResourceType.DESK);
    if (!deskState) return false;
    return !!(deskState.assignedTo || deskState.status?.reservedFor);
  }

  /**
   * Checks if the desk has an occupant OTHER than the current user.
   * Returns false if the current user is the occupant (they should see book buttons).
   * Returns true if someone else is the occupant (they should see occupied view).
   *
   * Handles two URN formats:
   * - assignedTo: urn:envoy:employee:email:{email}
   * - reservedFor: urn:envoy:employee:{employee_id}
   */
  @action
  hasOtherUserOccupant(deskId: string): boolean {
    const deskState = this.liveMap.getResourceStateByIdAndType(deskId, ResourceType.DESK);

    if (!deskState) {
      return false;
    }

    const hasOccupant = !!(deskState.assignedTo || deskState.status?.reservedFor);

    if (!hasOccupant) {
      return false;
    }

    // Get the full URN to check format before extraction
    const reservedForUrn = deskState.status?.reservedFor;
    const assignedToUrn = deskState.assignedTo;
    const urnToCheck = reservedForUrn || assignedToUrn;

    if (!urnToCheck) {
      return true;
    }

    // Check if the URN uses the email format: urn:envoy:employee:email:{email}
    if (urnToCheck.includes(':email:')) {
      // Email-based URN format
      const occupantEmail = getIdFromURN(urnToCheck);

      if (occupantEmail === this.currentAdmin.employee?.email) {
        return false; // Current user is the occupant
      }
    } else {
      // ID-based URN format: urn:envoy:employee:{employee_id}
      const occupantId = getIdFromURN(urnToCheck);

      if (occupantId === this.currentAdmin.employee?.id) {
        return false; // Current user is the occupant
      }
    }

    return true; // Someone else is the occupant
  }

  /**
   * Checks if the current desk has a reservation made by the current user
   */
  @action
  isReservedByCurrentUser(reservation: ReservationModel | undefined): boolean {
    if (!reservation) return false;

    const reservationUserId = reservation.belongsTo('user').id();
    return reservationUserId === this.state.currentUser?.id;
  }

  /**
   * Checks if the current desk is assigned to the current user
   */
  @action
  isAssignedToCurrentUser(desk: DeskModel | null): boolean {
    if (!desk) return false;

    return desk.assignedTo === this.state.currentUser?.email;
  }

  /**
   * Determines if the "Free up" button should be shown.
   * Show whenever the current user has a reservation, regardless of assignment status.
   * This allows users to free up their assigned desks if they've reserved them.
   */
  @action
  canFreeUp(reservation: ReservationModel | undefined, _desk: DeskModel | null): boolean {
    return this.isReservedByCurrentUser(reservation);
  }

  /**
   * Gets the employee data for displaying in the popup
   */
  @action
  getEmployeeData(
    employee: EmployeeModel | undefined,
    reservation: ReservationModel | undefined,
  ): { name: string; isCurrentUser: boolean; profilePictureUrl: string | null } | null {
    if (!employee) return null;

    const isCurrentUser = this.isReservedByCurrentUser(reservation);
    return {
      name: employee.name,
      isCurrentUser,
      profilePictureUrl: employee.profilePictureUrl || null,
    };
  }

  /**
   * Task to release/free up a desk reservation
   */
  releaseReservationTask = task({ drop: true }, async (reservation: ReservationModel): Promise<void> => {
    try {
      const url = urlBuilder.rms.releaseDesk();

      await this.ajax.request(url, {
        type: 'POST',
        contentType: 'application/vnd.api+json',
        data: {
          data: {
            attributes: {
              'reservation-id': reservation.id,
            },
          },
        },
      });

      this.store.unloadRecord(reservation);
      this.flashMessages.showAndHideFlash('success', 'Desk freed up successfully');

      // Reload reservations and available desks
      if (this.args.loadReservations) {
        this.args.loadReservations();
      }
      if (this.args.loadAvailableDesks) {
        this.args.loadAvailableDesks();
      }

      // Close the popup
      if (this.args.onCancel) {
        this.args.onCancel();
      }
    } catch (error) {
      this.flashMessages.showAndHideFlash('error', 'Failed to free up desk');
      throw error;
    }
  });

  get #locationTimeZone(): string {
    return this.state.currentLocation?.timezone || 'UTC';
  }

  // yyyy-MM-dd in the location's wall-clock — strings compare lexicographically
  // and match calendar order, so they double as date-comparison primitives
  // without any Date-arithmetic gymnastics. Using strings (rather than
  // toZonedTime + getTime()) keeps the cross-TZ semantics obvious: an admin in
  // Tokyo and one in NY both see the same office calendar boundary.
  get #todayKeyInLocation(): string {
    return formatInTimeZone(new Date(), this.#locationTimeZone, 'yyyy-MM-dd');
  }

  get #selectedDateKeyInLocation(): string {
    return formatInTimeZone(this.args.selectedDate, this.#locationTimeZone, 'yyyy-MM-dd');
  }

  // Memoize the live-map state lookup — getResourceStateByIdAndType is an
  // Array.find over every resource on the floor, and the three visibility
  // getters + several helpers all read it on every render. Tracked deps
  // (feature.externalId, liveMap.resourceStates) invalidate correctly.
  @cached
  get currentDeskState(): ReturnType<LiveMapService['getResourceStateByIdAndType']> {
    return this.liveMap.getResourceStateByIdAndType(this.args.feature.externalId, ResourceType.DESK);
  }

  get isSelectedDateInPast(): boolean {
    return this.#selectedDateKeyInLocation < this.#todayKeyInLocation;
  }

  // Admin lend/cancel/free-up ops are rejected by the backend for today + past —
  // the UI pre-filters so the menu items don't appear for those dates.
  get isSelectedDateTodayOrPast(): boolean {
    return this.#selectedDateKeyInLocation <= this.#todayKeyInLocation;
  }

  // Resolves the numeric employee id of the desk owner from the live map's
  // assignedTo URN + the employees ArrayProxy. Returns null when we can't
  // identify the owner — the admin-lend / cancel-lending flows need this id
  // to scope the backend action to the correct owner, so callers must bail
  // when null.
  //
  // Lender wins by design: admin-cancel-lending must scope to the employee
  // whose lending row exists, even if the desk has been reassigned to a
  // different owner mid-cycle. Prefer reverting/canceling the actual lender
  // over chasing the current assignment.
  getOwnerEmployeeId(deskId: string): number | null {
    const deskState = this.currentDeskState;

    // lender.id is a URN (e.g. "urn:envoy:employee:42"), not a bare id.
    const fromLender = toPositiveIntOrNull(getIdFromURN(deskState?.lender?.id));
    if (fromLender !== null) return fromLender;

    const assignedTo = deskState?.assignedTo;
    if (assignedTo?.includes(':email:')) {
      const email = getIdFromURN(assignedTo);
      if (email) {
        const fromAssignedTo = toPositiveIntOrNull(this.getEmployeeByEmail(email)?.id);
        if (fromAssignedTo !== null) return fromAssignedTo;
      }
    }

    const desk = this.getDeskById(deskId);
    if (desk?.assignedTo) {
      const fromDesk = toPositiveIntOrNull(this.getEmployeeByEmail(desk.assignedTo)?.id);
      if (fromDesk !== null) return fromDesk;
    }

    return null;
  }

  get canShowAdminLendAction(): boolean {
    if (!this.abilities.can('admin-lend desk for desks')) return false;
    if (this.isSelectedDateTodayOrPast) return false;

    const deskId = this.args.feature.externalId;
    const desk = this.getDeskById(deskId);
    if (!desk) return false;
    if (desk.enabled === false) return false;
    if (!desk.assignedTo) return false;
    if (this.isAssignedToCurrentUser(desk)) return false;

    // Require a known live-map state. Without it (BE error / not loaded yet)
    // the lender + reservedFor checks below would silently pass against
    // undefined, surfacing the action on a desk we can't reason about.
    const deskState = this.currentDeskState;
    if (!deskState) return false;
    if (deskState.lender) return false;

    if (this.getReservationByDeskId(deskId)) return false;
    if (deskState.status?.reservedFor) return false;

    return true;
  }

  get canShowAdminCancelLendingAction(): boolean {
    if (!this.abilities.can('admin-lend desk for desks')) return false;
    if (this.isSelectedDateTodayOrPast) return false;

    const deskId = this.args.feature.externalId;
    const desk = this.getDeskById(deskId);
    if (desk?.enabled === false) return false;

    const deskState = this.currentDeskState;
    if (!deskState?.lender) return false;

    if (this.getReservationByDeskId(deskId)) return false;
    if (deskState.status?.reservedFor) return false;

    return true;
  }

  get canShowAdminFreeUpAction(): boolean {
    if (!this.abilities.can('admin-free-up desk for desks')) return false;
    if (this.isSelectedDateTodayOrPast) return false;

    const deskId = this.args.feature.externalId;
    const desk = this.getDeskById(deskId);
    if (desk?.enabled === false) return false;

    const deskState = this.currentDeskState;
    if (!deskState) return false;
    // Free-up targets a renter on a *lent* desk. Without a lender the
    // reservation belongs to the owner using their own desk, which is not
    // an admin-free-up situation — only the owner themselves can drop it.
    if (!deskState.lender) return false;
    if (!desk?.assignedTo && !deskState.assignedTo) return false;

    const hasReservation = !!this.getReservationByDeskId(deskId) || !!deskState.status?.reservedFor;
    if (!hasReservation) return false;

    return this.hasOtherUserOccupant(deskId);
  }

  get anyAdminMoreMenuActionVisible(): boolean {
    return this.canShowAdminLendAction || this.canShowAdminCancelLendingAction || this.canShowAdminFreeUpAction;
  }

  get adminLendInitialDates(): Date[] {
    return [this.args.selectedDate];
  }

  // Map's selectedDate pre-populates the cancel-lending modal only when it's
  // actually one of the desk's lent dates — otherwise the admin opened the
  // popup on a non-lent day (e.g. a gap between lent days) and we want the
  // modal to start empty rather than with an un-selectable chip.
  get adminCancelLendingInitialDates(): Date[] {
    const key = formatInTimeZone(this.args.selectedDate, this.#locationTimeZone, 'yyyy-MM-dd');
    return this.adminCancelLendingAllowedDates.includes(key) ? [this.args.selectedDate] : [];
  }

  @action
  openAdminLendModal(): void {
    this.adminLendModalOpen = true;
  }

  @action
  closeAdminLendModal(): void {
    this.adminLendModalOpen = false;
  }

  @action
  closeAdminCancelLendingModal(): void {
    this.adminCancelLendingModalOpen = false;
    this.adminCancelLendingAllowedDates = [];
  }

  // Modal hands us yyyy-MM-dd strings (see MultiDateScheduleModal#lendTask).
  // We rebuild a Date whose wall-clock year/month/day match the key in the
  // browser's local TZ. The deskLending service then formats with
  // formatISO(date, {representation: 'date'}) which also reads local fields,
  // so the round-trip (location TZ → yyyy-MM-dd → local Date → yyyy-MM-dd)
  // is stable even when the admin's browser TZ differs from the location TZ.
  #parseDateKey(key: string): Date {
    const [year, month, day] = key.split('-').map(Number);
    return new Date(year!, month! - 1, day);
  }

  // Shared shape for the three admin mutations (lend, cancel-lending,
  // free-up). On preflight or mutation failure we flash + re-throw so the
  // caller (typically the modal's lendTask) can keep itself open and
  // preserve the admin's date selection.
  //
  // Post-mutation steps (success flash + refresh + close popup) are wrapped
  // in their own catch: the mutation already succeeded server-side, so an
  // error here must NOT present as if the mutation failed. Anything that
  // throws on the success path (a destroyed flash service, a rejected
  // refresh, an onCancel that synchronously throws) is logged but not
  // re-thrown — otherwise the modal's catch would absorb it and leave a
  // green success toast on top of a stuck modal.
  async #runAdminAction<T>(config: {
    preflight: () => T | null;
    preflightErrorMsg: string;
    mutate: (prep: T) => Promise<unknown>;
    successMsg: string;
    errorMsg: string | ((error: unknown) => string);
  }): Promise<void> {
    const prep = config.preflight();
    if (prep == null) {
      this.flashMessages.showAndHideFlash('error', config.preflightErrorMsg);
      throw new Error(config.preflightErrorMsg);
    }

    try {
      await config.mutate(prep);
    } catch (error) {
      const message = typeof config.errorMsg === 'function' ? config.errorMsg(error) : config.errorMsg;
      this.flashMessages.showAndHideFlash('error', message);
      throw error;
    }

    try {
      this.flashMessages.showAndHideFlash('success', config.successMsg);
      await this.liveMap.getUpdatedLiveMapData();
      this.args.onCancel?.();
    } catch (postSuccessError) {
      // eslint-disable-next-line no-console
      console.error('desk-popup: post-success cleanup failed after admin action', postSuccessError);
    }
  }

  adminLendTask = dropTask(async (dates: string[]): Promise<void> => {
    const deskId = this.args.feature.externalId;
    await this.#runAdminAction({
      preflight: () => this.getOwnerEmployeeId(deskId),
      preflightErrorMsg: "Couldn't identify desk owner",
      mutate: (ownerId) =>
        this.deskLending.adminLendDesk(
          deskId,
          dates.map((key) => this.#parseDateKey(key)),
          ownerId,
        ),
      successMsg: 'Desk lent for selected dates',
      // BE rejects with 409 DESK_HAS_PLANNING_ASSIGNMENTS when the desk is
      // already scheduled to someone on the selected dates. Surface a
      // specific message instead of the generic failure toast.
      errorMsg: (error) =>
        conflictDetail(error) === 'DESK_HAS_PLANNING_ASSIGNMENTS'
          ? "Can't lend desk, it has scheduled bookings on the selected dates."
          : 'Failed to lend desk',
    });
  });

  // Fetches the desk's currently-lent dates (both LENT_AVAILABLE and
  // LENT_BOOKED — the modal surfaces booked dates too, and submitting one
  // fails with 409 BOOKER_EXISTS which the task below handles). Fetch
  // happens on click rather than eagerly because this popup opens on every
  // desk click on the map; pre-fetching every desk would be wasteful.
  openAdminCancelLendingModalTask = dropTask(async (): Promise<void> => {
    const deskId = this.args.feature.externalId;
    // Pass raw Dates plus the location TZ — fetchLendingStatus formats the
    // window keys in location wall-clock so cross-TZ admins see the right
    // first/last day. BE caps the window inclusive at 90 days, so we send
    // [today, today + (N - 1)] to span exactly N calendar days.
    const start = new Date();
    const end = addDays(start, DeskPopup.CANCEL_LENDING_LOOKUP_DAYS - 1);

    try {
      const response = await this.deskLending.fetchLendingStatus(deskId, start, end, this.#locationTimeZone);
      // Popup may have been closed (or the user clicked a different desk)
      // while the fetch was in flight — drop the result instead of writing
      // tracked props on a destroyed component.
      if (this.isDestroyed || this.isDestroying) return;
      this.adminCancelLendingAllowedDates = response.dates
        .filter(
          (d) => d.lendingStatus === LENDING_STATUS.LENT_AVAILABLE || d.lendingStatus === LENDING_STATUS.LENT_BOOKED,
        )
        .map((d) => d.date);
      this.adminCancelLendingModalOpen = true;
    } catch {
      if (this.isDestroyed || this.isDestroying) return;
      this.flashMessages.showAndHideFlash('error', "Couldn't load lending status");
    }
  });

  adminCancelLendingTask = dropTask(async (dates: string[]): Promise<void> => {
    const deskId = this.args.feature.externalId;
    await this.#runAdminAction({
      preflight: () => this.getOwnerEmployeeId(deskId),
      preflightErrorMsg: "Couldn't identify desk owner",
      mutate: (ownerId) =>
        this.deskLending.adminCancelLending(
          deskId,
          dates.map((key) => this.#parseDateKey(key)),
          ownerId,
        ),
      successMsg: 'Desk lending canceled',
      // BE rejects with 409 when failIfBooked: true and any selected date is
      // booked. Surface a specific message so the admin knows to free up the
      // booker first instead of seeing the generic failure toast.
      errorMsg: (error) =>
        error instanceof ConflictError
          ? "Can't cancel lending, one or more dates are booked. Free up the booker first."
          : 'Failed to cancel desk lending',
    });
  });

  // Prefers the reservation model (loaded on the page) over the live map's
  // reservationId so we stay aligned with what's in the store, but falls
  // back to the live map when the reservation record hasn't been loaded
  // client-side (common for admin-only views that don't hydrate the store).
  #resolveReservationId(deskId: string): number | null {
    const fromReservation = toPositiveIntOrNull(this.getReservationByDeskId(deskId)?.id);
    if (fromReservation !== null) return fromReservation;

    return toPositiveIntOrNull(this.currentDeskState?.status?.reservationId);
  }

  adminFreeUpDeskTask = dropTask(async (): Promise<void> => {
    const deskId = this.args.feature.externalId;
    // Free-up is invoked directly from the dropdown click, no modal layer
    // to await this task. ember-concurrency surfaces an unhandled rethrow
    // as a global error, so we swallow it here. The only thing
    // #runAdminAction re-throws is the (already-flashed) preflight or
    // mutation error; success-path errors are caught inside it and never
    // reach this catch.
    try {
      await this.#runAdminAction({
        preflight: () => this.#resolveReservationId(deskId),
        preflightErrorMsg: "Couldn't identify the booker's reservation",
        mutate: (reservationId) => this.deskLending.adminCancelReservation(reservationId),
        successMsg: 'Desk freed up',
        errorMsg: 'Failed to free up desk',
      });
    } catch {
      // intentional — see comment above.
    }
  });
}
